import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { LeadTimeModiferType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"

import Swal from "sweetalert2"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { leadTimeModifier: LeadTimeModiferType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: LeadTimeModiferType) => void
  handleFormUpdate: (form: LeadTimeModiferType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const LeadTimeModifier: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<LeadTimeModiferType>()

  const onSubmit = (data: LeadTimeModiferType) => {
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      // color: "#ffffff",
      // background: "#333333",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: LeadTimeModiferType) => {
    props.setTitle('Update Lead Time Modifer')
    setValue("id", e.id)
    setValue("leadTimeDays", e.leadTimeDays)
    setValue("leadTimeValue", e.leadTimeValue)
    setValue("datLowMod", e.datLowMod)
    setValue("datAvgMod", e.datAvgMod)
    setValue("datHighMod", e.datHighMod)
    setValue("equipmentType", e.equipmentType)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      id: undefined,
      equipmentType: undefined,
      leadTimeDays: undefined,
      leadTimeValue: undefined,
      datLowMod: undefined,
      datAvgMod: undefined,
      datHighMod: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title={props.title}
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">
                Equipment Type
              </label>
              <select
                className={
                  (errors.equipmentType ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white text-light-2 border-2 rounded"
                }
                {...register("equipmentType", { required: true })}
              >
                <option value="">Select Equipment</option>
                <option value="FLATBED">Flatbed</option>
                <option value="VAN">Van</option>
                <option value="REEFER">Reefer</option>
              </select>
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Lead Time Days</label>
              <input
                className={
                  (errors.leadTimeDays ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Lead Time Days"
                {...register("leadTimeDays", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Lead Time Value</label>
              <input
                className={
                  (errors.leadTimeValue
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Lead Time Value"
                {...register("leadTimeValue", { required: true })}
              />
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Dat Low Mod</label>
              <input
                className={
                  (errors.datLowMod ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Dat Low Mod"
                {...register("datLowMod", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Dat Avg Mod</label>
              <input
                className={
                  (errors.datAvgMod ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Dat Avg Mod"
                {...register("datAvgMod", { required: true })}
              />
            </fieldset>
          </div>
          <fieldset>
            <label className="text-xs dark:text-primary-8 text-light-1">Dat High Mod</label>
            <input
              className={
                (errors.datHighMod ? "border-red-400" : "dark:border-secondary-2") +
                " w-full text-sm p-2 dark:bg-transparent outline-none dark:text-white bg-white text-light-2 border-2 rounded placeholder:text-xs"
              }
              type="number"
              placeholder="Enter Dat High Mod"
              {...register("datHighMod", { required: true })}
            />
          </fieldset>
          <button
            onClick={handleSubmit(onSubmit)}
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.leadTimeModifier.map((e, i) => (
            <tr key={i} className="text-left">
              <td className="whitespace-nowrap p-4 w-52 font-medium">
                {e.equipmentType}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.leadTimeDays}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.leadTimeValue}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.datLowMod}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.datAvgMod}</td>

              <td className={"whitespace-nowrap p-4 w-52"}>{e.datHighMod}</td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
